<script lang="ts">
  // A compare's page (docs/design.md "Compares"): its header, then the
  // range timeline and the Files view, or the Commits view. The store
  // (state/compare.svelte.ts) is the views' host, a BranchHost: no
  // conversation, so no threads, drafts or composer. As on the pull
  // request's page, one diff model per comparison the URL names: a new
  // one loads beside the one shown, which stays until it is ready.
  import { onDestroy, untrack } from 'svelte';
  import { toast } from '../lib/state/toast.svelte';
  import { comparisonOf, timelineOf } from '../lib/logic/range';
  import { facets, subjectKey } from '../lib/subject';
  import { repoPath, subjectPath, type CompareRoute } from '../lib/router';
  import { CompareStore } from '../lib/state/compare.svelte';
  import { DiffModel } from '../lib/state/diffs.svelte';
  import { nav } from '../lib/nav.svelte';
  import { prefs } from '../lib/state/prefs.svelte';
  import { algorithmOf } from '../lib/diff/algorithms';
  import { carryFilter } from '../lib/logic/filefilter';
  import { COMPARE_GUIDE } from '../lib/keys/guides';
  import FindFile from '../lib/components/FindFile.svelte';
  import { findFile } from '../lib/state/findFile.svelte';
  import { carryOut, keyboard, useKeys } from '../lib/keys/keyboard.svelte';
  import { updateBranchPage, type BranchPageEffect } from '../lib/commands/page';
  import { session } from '../lib/state/session.svelte';
  import { subjectKey as relayKey } from '../lib/api/relaySubject';
  import type { RelayPage } from '../lib/components/RelayThread.svelte';
  import Rail from '../lib/components/Rail.svelte';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import PaneBoundary from '../lib/components/PaneBoundary.svelte';
  import CompareHeader from '../lib/components/CompareHeader.svelte';
  import RangeTimeline from '../lib/components/RangeTimeline.svelte';
  import RelayApproval from '../lib/components/RelayApproval.svelte';
  import Files from './Files.svelte';
  import Commits from './Commits.svelte';

  interface Props {
    route: CompareRoute;
    /** The page's store; one is made for the route's subject unless given (tests). */
    store?: CompareStore;
  }
  let { route, store: given }: Props = $props();

  // svelte-ignore state_referenced_locally
  const store = given ?? new CompareStore(route.subject);
  // svelte-ignore state_referenced_locally
  if (!given) void store.open();
  onDestroy(() => {
    next?.dispose();
    model?.dispose();
    if (!given) store.dispose();
    // The toast is the app's (ToastRegion): what this page raised goes with it, as a pull request's page does.
    toast.dismiss();
  });

  const view = $derived(route.view);
  const has = $derived(facets(store.subject));
  const label = $derived(subjectKey(store.subject).replace(/^[^@]*@/, ''));

  $effect(() => {
    document.title = `${view === 'commits' ? 'Commits · ' : ''}${label} · ${store.subject.owner}/${store.subject.repo}`;
  });

  // The comparison the URL names, read as the timeline reads it.
  const asked = $derived(comparisonOf(timelineOf(store.track, has), nav.query));
  const ws = $derived(nav.query.get('ws') === 'ignore');
  const algorithm = $derived(algorithmOf(prefs.value.diffAlgorithm));
  let model: DiffModel | null = $state(null);
  let next: DiffModel | null = $state(null);
  let modelKey = '';
  // Bumped to rebuild the diff for a head that moved since it was built.
  let diffRevision = $state(0);
  $effect(() => {
    if (store.status !== 'ready') return;
    const key = `${asked.from}|${asked.to}|${ws}|${algorithm}|${diffRevision}`;
    if (key === modelKey) return;
    modelKey = key;
    const m = new DiffModel(store, { from: asked.from, to: asked.to, ignoreWhitespace: ws, algorithm });
    untrack(() => {
      next?.dispose();
      if (!model) {
        model = m;
        void m.init();
        return;
      }
      next = m;
      void m.init().then(() => {
        if (m !== next) return;
        const old = model;
        model = m;
        next = null;
        old?.dispose();
      });
    });
  });

  // The review model follows the marks, for the comparison shown and the one loading to replace it.
  $effect(() => {
    const anchors = store.marks.anchors;
    for (const m of [model, next] as (DiffModel | null)[]) if (m) m.setMarks(anchors);
  });

  // The diff on screen is the Files view's while it shows it.
  $effect(() => {
    const m = model as DiffModel | null;
    if (view !== 'files' || !m) return;
    untrack(() => m.show());
    return () => m.hide();
  });

  /** The head moved since the diff on screen was built (a push read on focus). */
  const headMoved = $derived.by(() => {
    const m = model as DiffModel | null;
    return !!m?.head && !!store.compare && m.head !== store.compare.head;
  });

  // The pull requests that brought each commit in, read once the Commits view shows (a GraphQL read, so not before).
  $effect(() => {
    if (view !== 'commits' || store.status !== 'ready') return;
    void store.track.head;
    untrack(() => void store.loadLinked());
  });

  /** The page's content, where Find file reads selections. */
  let mainEl: HTMLElement | undefined = $state();
  /** The compare's files present at its head. */
  const headFiles = $derived(new Set((store.files ?? []).filter((f) => f.status !== 'removed').map((f) => f.filename)));

  const showTimeline = $derived(store.track.main.length > 1 || nav.query.has('from') || nav.query.has('to'));

  /** The page as the agent panel reads it: the compare, its head, where its agents are managed. */
  const ask: RelayPage | null = $derived(
    session.agents
      ? {
          subject: relayKey(store.subject),
          head: store.compare?.head ?? null,
          viewer: session.user?.login ?? '',
          agents: { at: 'here', where: 'in this compare’s header' },
          noun: 'compare',
          draftsOff: true,
          draftsOffText: null,
        }
      : null,
  );

  useKeys({
    scope: 'pullRequest',
    guide: COMPARE_GUIDE,
    handle: (command) => carryOut(updateBranchPage({ view, noun: 'compare' }, command)[1], perform),
  });

  /** The header's picker open, by key: the base's or the head's. */
  let picking: 'base' | 'head' | null = $state(null);

  function perform(e: BranchPageEffect) {
    switch (e.kind) {
      case 'navigate':
        return nav.go(carryFilter(subjectPath(store.subject, e.view), nav.query));
      case 'scroll-window':
        return window.scrollTo({ top: e.to === 'top' ? 0 : document.body.scrollHeight, behavior: 'instant' });
      case 'find-file':
        return findFile.fromSelection();
      case 'show-guide':
        return keyboard.showGuide();
      case 'pick':
        return void (picking = e.end);
    }
  }
</script>

<div class="shell">
  <Rail here={{ subject: store.subject, base: store.compare?.baseRef ?? null }} />
  <main bind:this={mainEl}>
    <LoadingGate phase={store.status === 'loading' ? 'loading' : store.status === 'error' ? 'failed' : 'ready'} view={store.loadView()} onretry={() => store.load()}>
      {#if store.status === 'notfound'}
        <div class="center-msg">
          <h1>Compare not found</h1>
          <p class="muted">{store.notFound} Changeling sees exactly what your GitHub account can see.</p>
          <a class="btn" href={repoPath(store.subject.owner, store.subject.repo)}>Go to {store.subject.owner}/{store.subject.repo}</a>
        </div>
      {:else if store.compare}
        {#if store.gone}
          <div class="banner" role="status">{store.gone} Showing it as it was last read.</div>
        {/if}
        {#if store.unrecorded}
          <div class="banner" role="status">{store.serverDataText}</div>
        {/if}
        <PaneBoundary what="the compare's header"><CompareHeader {store} {view} bind:picking /></PaneBoundary>
        {#if session.agents}<PaneBoundary what="the agent approvals"><RelayApproval subject={relayKey(store.subject)} /></PaneBoundary>{/if}
        {#if view === 'commits'}
          <PaneBoundary what="the commits">
            <Commits
              track={store.track}
              facets={has}
              filesPath={subjectPath(store.subject)}
              linked={(sha) => store.linked.get(sha)}
              linkedError={store.linkedError}
              held={store.compare.truncated ? { held: store.compare.commits.length, total: store.compare.totalCommits } : null}
            />
          </PaneBoundary>
        {:else if store.compare.status === 'identical' || (!store.compare.files.length && !store.compare.aheadBy)}
          <div class="center-msg">
            <h2>Nothing to compare</h2>
            <p class="muted">{store.compare.baseRef} already holds everything on {store.subject.head}.</p>
          </div>
        {:else if model}
          {#if headMoved}
            <div class="banner" role="status">
              New commits were pushed to {store.subject.head} since this diff was loaded.
              <button class="btn small" onclick={() => diffRevision++}>Reload diff</button>
            </div>
          {/if}
          {#if store.filesMissing === null}
            <div class="banner" role="status">
              GitHub lists at most 300 files of a compare, and this repository's trees are too large to list the rest: some changed files are not shown.
            </div>
          {/if}
          {#if showTimeline}<RangeTimeline track={store.track} facets={has} />{/if}
          <PaneBoundary what="the files"><Files host={store} {model} updating={!!next} {ask} /></PaneBoundary>
        {/if}
      {/if}
    </LoadingGate>
  </main>
</div>

{#if store.compare}
  <FindFile
    owner={store.subject.owner}
    repo={store.subject.repo}
    filesPath={subjectPath(store.subject)}
    tree={model?.repoTree ?? null}
    changed={headFiles}
    webUrl={session.config?.github_web_url ?? 'https://github.com'}
    root={mainEl ?? null}
  />
{/if}
